import { useDeleteAgentImmediately } from "hooks/useAgents";
import {
  AgentListResponse,
  deleteAgentImmediatelyInput,
} from "services/interface";
import { EllipsisOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Menu,
  MenuProps,
  Modal,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { ColumnsType } from "antd/es/table";
import React, { Dispatch, FC, SetStateAction } from "react";

const { confirm } = Modal;

interface TableAgentListProps {
  data: AgentListResponse[] | undefined;
  isLoading: boolean;
  refetch: () => void;
  limit: number;
  offset: number;
  setLimit: Dispatch<SetStateAction<number>>;
  setOffset: Dispatch<SetStateAction<number>>;
}

const TableAgentList: FC<TableAgentListProps> = ({
  data,
  isLoading,
  refetch,
  limit,
  offset,
  setLimit,
  setOffset,
}) => {
  const { mutate: deleteAgent } = useDeleteAgentImmediately();
  const showDeleteConfirm = (id: string) => {
    if (!id) return;
    const data: deleteAgentImmediatelyInput = {
      ref_id: id,
      ref_type: "agent",
      remark: "ยื่นคำร้องขอลบบัญชีผู้ใช้",
    };
    confirm({
      title: "คุณต้องการลบผู้ใช้งานนี้ใช่หรือไม่",
      icon: <ExclamationCircleFilled />,
      content: "ผู้ใช้งานนี้จะหายไปจากระบบ และ ไม่สามารถกู้คืนได้",
      okText: "ใช่, ต้องการลบ",
      okType: "danger",
      cancelText: "ไม่, ยกเลิกการลบ",
      onOk() {
        deleteAgent(data, {
          onSuccess: (data) => {
            if (data.status === "success") {
              message.success("ลบผู้ใช้งานสำเร็จ");
              refetch();
            } else {
              message.error(`${data.error_message?.[0]}`);
            }
          },
          onError: () => {
            message.error("ลบผู้ใช้งานไม่สำเร็จ");
          },
        });
      },
      onCancel() {
        //console.log("Cancel");
      },
    });
  };

  const columns: ColumnsType<AgentListResponse> = [
    // ลำดับ
    {
      title: "ลำดับ",
      dataIndex: "id",
      width: 50,
      render: (_text, _record, index) => {
        return <Typography.Paragraph>{index + 1}</Typography.Paragraph>;
      },
    },
    {
      title: "รหัสพนักงาน",
      dataIndex: "agent_code",
      sorter: (a, b) => a.agent_code.localeCompare(b.agent_code),
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    // ชื่อ
    {
      title: "ชื่อ",
      dataIndex: "agent_name",
      sorter: (a, b) => a.agent_name.localeCompare(b.agent_name),
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    // {
    //   title: "โรงพยาบาลที่สังกัด",
    //   dataIndex: "hospital_name",
    //   sorter: (a, b) => a.hospital_name.localeCompare(b.hospital_name),
    //   render: (_text) => {
    //     return <Typography.Paragraph>{_text}</Typography.Paragraph>;
    //   },
    // },
    // เบอร์โทร
    {
      title: "เบอร์โทร",
      dataIndex: "telephone",
      // sorter: (a, b) => a.telephone.localeCompare(b.telephone),
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    {
      title: "แผนก",
      dataIndex: "department_name",
      sorter: (a, b) => a.department_name.localeCompare(b.department_name),
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    // จำนวนคำสั่งซื้อ
    {
      title: "จำนวนที่ขาย",
      dataIndex: "count_sale",
      sorter: (a, b) => parseInt(a.count_sale) - parseInt(b.count_sale),
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    //มูลค่าขายรวมs
    {
      title: "มูลค่าขายรวม",
      dataIndex: "total_sale_price",
      sorter: (a, b) => a.total_sale_price - b.total_sale_price,
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    //ค่าคอมมิชชั่น
    {
      title: "ค่าคอมมิชชั่น",
      dataIndex: "sale_commission",
      sorter: (a, b) => a.sale_commission - b.sale_commission,
      render: (_text) => {
        return <Typography.Paragraph>{_text}</Typography.Paragraph>;
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",
      key: "id",
      width: 100,
      render: (id) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <Space size="middle">
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="1"
                    onClick={() => {
                      showDeleteConfirm(id);
                    }}
                  >
                    ลบผู้ใช้
                  </Menu.Item>
                </Menu>
              }
            >
              <div>
                <EllipsisOutlined />
              </div>
            </Dropdown>
            {/* <Button
                className="product-btn"
                danger
                style={{ 
                  width: 50, 
                }}
                onClick={() => showDeleteConfirm(id)} 
              >
                ลบ
              </Button> */}
          </Space>
        </div>
      ),
    },
  ];
  return (
    <div>
      {/* rowSelection={rowSelection} */}
      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={{
          pageSize: limit,
          total: parseInt(data?.[0]?.full_count || "0"),
          onChange: (page, pageSize) => {
            setOffset(pageSize! * (page! - 1));
            setLimit(pageSize!);
          },
        }}
      />
    </div>
  );
};

export default TableAgentList;
